import { useState } from 'react';
import { cn } from '@/lib/cn';
import { rulesFor, type RuleStandard, type RuleSource } from '@/lib/nemsis-elements';

/**
 * RuleTag — Foundation §8.7 rule-source pill. Shown beside every
 * compliance-driven field so the user can see which standard(s)
 * require the data point, and tap to read a plain-language
 * "why is this here?" explanation.
 *
 * Two usage modes:
 *   <RuleTag elementId="eHistory.17" />
 *     — looks up every standard that cites the element.
 *
 *   <RuleTag sources={[{standard:'ACS-2021', rationale:'...'}]} />
 *     — render ad-hoc rule tags (e.g. for whole conditional sets).
 *
 * Color mapping follows Foundation §8.2 (rule source tags):
 *   NEMSIS Required   amber
 *   NEMSIS Mandatory  red
 *   RIDOH             violet
 *   CMS               green
 *   Department        blue
 *   ACS-2021          amber (clinical best-practice)
 */

interface Props {
  elementId?: string;
  sources?: RuleSource[];
  /** Compact mode hides the detail card and shows only the pill. */
  compact?: boolean;
  className?: string;
}

const STYLES: Record<RuleStandard, { pill: string; dot: string }> = {
  NEMSIS:      { pill: 'text-amber-300 border-amber-700 bg-amber-950/40',     dot: 'bg-amber-400' },
  RIDOH:       { pill: 'text-violet-300 border-violet-700 bg-violet-950/40', dot: 'bg-violet-400' },
  CMS:         { pill: 'text-emerald-300 border-emerald-700 bg-emerald-950/40', dot: 'bg-emerald-400' },
  Department:  { pill: 'text-blue-300 border-blue-700 bg-blue-950/40',        dot: 'bg-blue-400' },
  'ACS-2021':  { pill: 'text-amber-300 border-amber-700 bg-amber-950/40',    dot: 'bg-amber-400' },
  Clinical:    { pill: 'text-slate-300 border-slate-600 bg-slate-800',       dot: 'bg-slate-400' },
  FEMA:        { pill: 'text-cyan-300 border-cyan-700 bg-cyan-950/40',       dot: 'bg-cyan-400' },
  EMSC:        { pill: 'text-pink-300 border-pink-700 bg-pink-950/40',       dot: 'bg-pink-400' },
  'ME/LE':     { pill: 'text-slate-200 border-slate-500 bg-slate-800',       dot: 'bg-slate-300' },
  NHTSA:       { pill: 'text-yellow-300 border-yellow-700 bg-yellow-950/40', dot: 'bg-yellow-400' },
  NERIS:       { pill: 'text-orange-300 border-orange-700 bg-orange-950/40', dot: 'bg-orange-400' },
  OSHA:        { pill: 'text-rose-300 border-rose-700 bg-rose-950/40',       dot: 'bg-rose-400' },
};

export function RuleTag({ elementId, sources, compact, className }: Props) {
  const [open, setOpen] = useState(false);
  const resolved = sources ?? (elementId ? rulesFor(elementId) : []);
  if (resolved.length === 0) return null;

  // NEMSIS Mandatory gets a red emphasis regardless of the default amber.
  const primary = resolved[0];
  const isMandatory = primary.standard === 'NEMSIS' && primary.usage === 'Mandatory';
  const primaryStyle = isMandatory
    ? { pill: 'text-red-300 border-red-700 bg-red-950/40', dot: 'bg-red-400' }
    : STYLES[primary.standard];

  const label = primary.standard === 'NEMSIS' && primary.usage
    ? `NEMSIS ${primary.usage}`
    : primary.standard;

  return (
    <span className={cn('relative inline-block', className)}>
      <button
        type="button"
        onClick={() => !compact && setOpen((v) => !v)}
        className={cn(
          'inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-[0.08em] border leading-none',
          primaryStyle.pill,
          compact && 'cursor-default',
        )}
        aria-label={`Rule source: ${label}`}
      >
        <span className={cn('inline-block h-1 w-1 rounded-full', primaryStyle.dot)} />
        {primary.element ?? label}
        {resolved.length > 1 && (
          <span className="ml-0.5 opacity-60">+{resolved.length - 1}</span>
        )}
      </button>
      {open && !compact && (
        <div
          className="absolute z-40 mt-1 w-80 max-w-[90vw] border border-slate-700 bg-slate-900 p-2.5 shadow-lg text-left"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">
            Rule sources
          </div>
          <ul className="space-y-1.5">
            {resolved.map((s, i) => {
              const style = s.standard === 'NEMSIS' && s.usage === 'Mandatory'
                ? STYLES.NEMSIS
                : STYLES[s.standard];
              return (
                <li key={i} className="flex gap-2 items-start text-xs">
                  <span className={cn('mt-1 h-1.5 w-1.5 rounded-full shrink-0', style.dot)} />
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-200">
                      {s.standard}
                      {s.element && <span className="text-slate-500 font-mono ml-1">· {s.element}</span>}
                      {s.usage && <span className="text-slate-500 ml-1">· {s.usage}</span>}
                    </div>
                    {s.rationale && (
                      <div className="text-slate-400 text-[11px] leading-snug mt-0.5">
                        {s.rationale}
                      </div>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
          <button
            onClick={() => setOpen(false)}
            className="mt-2 text-[10px] font-mono uppercase tracking-widest text-slate-500 hover:text-slate-300"
          >
            Close
          </button>
        </div>
      )}
    </span>
  );
}
